class LandingProduct extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this._added = false;
    this._pending = false;
    this._stickyEl = this.querySelector('[data-sticky-bar]');
    this._pillsEl = this.querySelector('.landing-product__pills');
    this._buttons = Array.prototype.slice.call(
      this.querySelectorAll('[data-add-to-cart], [data-sticky-add-to-cart]')
    );
    this._buttons.forEach((btn) => {
      btn.addEventListener('click', (evt) => this._onButtonClick(evt));
    });
    this._basePath = this.dataset.basePath || '';
    this._viewQuery = this.dataset.viewQuery || '';
    this._variantPickerEl = this.querySelector('[data-variant-picker]');
    this._priceEl = this.querySelector('[data-price-amount]');
    this._variants = this._readVariantData();
    this._wireStickyVisibility();
    this._wireSiblingPills();
    this._wireVariantPicker();
    this._markDisabledOptions();
    this._syncAvailability();
  }

  // Mirrors setDisabled() in product.js: product-option.liquid renders data-available,
  // but the greyed-out styling comes from the .is-disabled class, which is applied here.
  _markDisabledOptions() {
    if (!this._variantPickerEl) return;
    this._variantPickerEl.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.classList.toggle('is-disabled', input.dataset.available === 'false');
    });
  }

  _currentVariant() {
    if (!this._variants) return null;
    const chosen = this._selectedOptionValues();
    if (!chosen.length) return null;
    return (
      this._variants.find(
        (v) =>
          Array.isArray(v.options) &&
          v.options.length === chosen.length &&
          v.options.every((opt, i) => opt === chosen[i])
      ) || null
    );
  }

  _syncAvailability() {
    const variant = this._currentVariant();
    // Falls back to the server-rendered flag when there's no picker (single-variant
    // product, or the size picker turned off in section settings).
    this._available = variant ? !!variant.available : this.dataset.variantAvailable !== 'false';
    this._buttons.forEach((btn) => {
      btn.disabled = !this._available;
      btn.classList.toggle('sold-out', !this._available);
    });
    this._updateButtons();
  }

  _readVariantData() {
    const el = this.querySelector('[data-variant-data]');
    if (!el) return null;
    try {
      const parsed = JSON.parse(el.textContent);
      return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  _wireVariantPicker() {
    if (!this._variantPickerEl || !this._variants) return;
    this._variantPickerEl.addEventListener('change', (evt) => {
      if (evt.target.type === 'radio') this._onVariantChange();
    });
  }

  _selectedOptionValues() {
    if (!this._variantPickerEl) return [];
    return Array.prototype.slice
      .call(this._variantPickerEl.querySelectorAll('input[type="radio"]:checked'))
      .map((input) => input.value);
  }

  // Re-apply a size chosen before a colourway swap, so switching colour doesn't
  // silently reset the shopper back to the smallest size.
  applySelectedOptions(values) {
    if (!this._variantPickerEl || !values || !values.length) return;
    const inputs = Array.prototype.slice.call(
      this._variantPickerEl.querySelectorAll('input[type="radio"]')
    );
    let changed = false;
    values.forEach((value) => {
      const match = inputs.find((input) => input.value === value);
      if (match && !match.checked) {
        match.checked = true;
        changed = true;
      }
    });
    if (changed) this._onVariantChange();
  }

  _onVariantChange() {
    const chosen = this._selectedOptionValues();
    if (!chosen.length) return;

    const match = this._variants.find(
      (v) =>
        Array.isArray(v.options) &&
        v.options.length === chosen.length &&
        v.options.every((opt, i) => opt === chosen[i])
    );
    if (!match) return;

    this.dataset.variantId = match.id;
    this.dataset.variantAvailable = match.available;
    if (this._priceEl) this._priceEl.textContent = match.price;

    // Changing size means the "Checkout →" state no longer refers to what's selected,
    // so drop back to "Add to bag" for the newly chosen variant.
    this._added = false;
    this._syncAvailability();
  }

  disconnectedCallback() {
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._siblingAbortController) this._siblingAbortController.abort();
  }

  _onButtonClick(evt) {
    if (this._added) {
      this._goToCheckout();
    } else {
      this._addToCart(evt.currentTarget);
    }
  }

  _addToCart(triggerBtn) {
    const variantId = this.dataset.variantId;
    if (!variantId || this._pending || this._available === false) return;
    this._pending = true;
    this._buttons.forEach((btn) => { btn.disabled = true; });

    fetch(this.dataset.cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then((res) => res.json().then((body) => ({ ok: res.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) {
          // Shopify explains *why* (e.g. sold out) in `description` — surface that rather
          // than a generic failure, which previously masked sold-out errors.
          const err = new Error((body && body.description) || 'Add to cart failed');
          err.userMessage = (body && body.description) || null;
          throw err;
        }
        this._added = true;
        this._updateButtons();
        this._showToast('Added to bag — ' + (this.dataset.productTitle || '') + '  ·  checkout below');
        document.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true, detail: { item: body } }));
      })
      .catch((err) => {
        this._showToast((err && err.userMessage) || 'Something went wrong — please try again');
      })
      .finally(() => {
        this._pending = false;
        // Re-enable only what's actually purchasable — a sold-out variant must stay disabled.
        this._syncAvailability();
      });
  }

  _updateButtons() {
    const soldOutLabel =
      (window.theme && theme.variantStrings && theme.variantStrings.soldOut) || 'Sold out';
    this._buttons.forEach((btn) => {
      btn.classList.toggle('is-added', this._added);
      if (this._available === false) {
        btn.textContent = soldOutLabel;
      } else {
        btn.textContent = this._added ? btn.dataset.labelAdded : btn.dataset.labelAdd;
      }
    });
  }

  _goToCheckout() {
    const openEvent = new CustomEvent('cart-drawer:open', { bubbles: true, cancelable: true });
    const handled = !document.dispatchEvent(openEvent);
    if (!handled && this.dataset.checkoutUrl) {
      window.location.href = this.dataset.checkoutUrl;
    }
  }

  _showToast(message) {
    let el = LandingProduct._toastEl;
    if (!el) {
      el = document.createElement('div');
      el.className = 'landing-product__toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
      LandingProduct._toastEl = el;
    }
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(LandingProduct._toastTimer);
    LandingProduct._toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2400);
  }

  _wireStickyVisibility() {
    if (!this._stickyEl) return;
    this._onScroll = () => {
      const threshold = window.innerHeight * 0.015;
      const scrolled = window.scrollY || window.pageYOffset;
      this._stickyEl.classList.toggle('is-visible', scrolled > threshold);
    };
    this._onResize = this._onScroll;
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });
    this._onScroll();
  }

  _wireSiblingPills() {
    if (!this._pillsEl) return;
    this._pillsEl.addEventListener('click', (evt) => {
      const pill = evt.target.closest('[data-sibling-handle]');
      if (!pill || !this._pillsEl.contains(pill) || pill.getAttribute('aria-disabled') === 'true') return;
      evt.preventDefault();
      this._switchSibling(pill.dataset.siblingHandle, pill.getAttribute('href'));
    });
  }

  _switchSibling(handle, fallbackUrl) {
    if (!handle || this._pillsEl.classList.contains('is-loading')) return;
    if (this._siblingAbortController) this._siblingAbortController.abort();
    this._siblingAbortController = new AbortController();

    const sectionId = this.id;
    // Keep ?view=landing (or whatever alternate template is active) — without it the
    // sibling URL renders the page's default template, which has no landing-product.
    const targetPath = this._basePath + '/' + handle + this._viewQuery;
    const url = new URL(targetPath, window.location.origin);
    url.searchParams.set('section_id', sectionId);

    this._pillsEl.classList.add('is-loading');
    const scrollY = window.scrollY;
    const preservedOptions = this._selectedOptionValues();

    fetch(url.toString(), { signal: this._siblingAbortController.signal })
      .then((res) => res.text())
      .then((responseText) => {
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        const newSection = doc.getElementById('shopify-section-' + sectionId);
        const currentSection = document.getElementById('shopify-section-' + sectionId);
        if (!newSection || !currentSection) {
          window.location.href = fallbackUrl;
          return;
        }

        currentSection.style.visibility = 'hidden';
        currentSection.innerHTML = newSection.innerHTML;
        window.history.replaceState({}, '', targetPath);

        const newRoot = currentSection.querySelector('landing-product');
        if (newRoot && typeof newRoot.applySelectedOptions === 'function') {
          newRoot.applySelectedOptions(preservedOptions);
        }

        requestAnimationFrame(() => {
          currentSection.style.visibility = '';
          window.scrollTo({ top: scrollY, behavior: 'instant' });
        });
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          window.location.href = fallbackUrl;
        }
      });
  }
}

if (!customElements.get('landing-product')) {
  customElements.define('landing-product', LandingProduct);
}
